import React, { useCallback, useEffect, useState } from 'react';
import Alert from '@cloudscape-design/components/alert';
import Badge from '@cloudscape-design/components/badge';
import Box from '@cloudscape-design/components/box';
import Button from '@cloudscape-design/components/button';
import Container from '@cloudscape-design/components/container';
import ContentLayout from '@cloudscape-design/components/content-layout';
import Header from '@cloudscape-design/components/header';
import Link from '@cloudscape-design/components/link';
import SpaceBetween from '@cloudscape-design/components/space-between';
import { useNavigate, useParams } from 'react-router-dom';
import { apiJson } from '../utils/api';

interface StorySource {
  title: string;
  url: string;
}

interface StoryDetail {
  type_key: string;
  make?: string;
  model?: string;
  category?: string;
  story?: string;
  story_highlights?: string[];
  notable_missions?: string[];
  story_sources?: StorySource[];
  story_status?: string;
  story_generated_at?: string;
  story_model?: string;
  story_judge?: {
    approved?: boolean;
    reason?: string;
    verbatim_overlap?: string | null;
    judged_at?: string;
  };
}

const StoryDetailPage: React.FC = () => {
  const { typeKey = '' } = useParams();
  const navigate = useNavigate();
  const [story, setStory] = useState<StoryDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async (): Promise<void> => {
    setLoading(true);
    setError(null);
    try {
      const data = await apiJson<StoryDetail>(`/admin/stories/${encodeURIComponent(typeKey)}`);
      setStory(data);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setLoading(false);
    }
  }, [typeKey]);

  useEffect(() => {
    void load();
  }, [load]);

  const setStatus = async (newStatus: string): Promise<void> => {
    setBusy(true);
    try {
      await apiJson(`/admin/stories/${encodeURIComponent(typeKey)}`, {
        method: 'POST',
        body: JSON.stringify({ story_status: newStatus }),
      });
      await load();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const published = (story?.story_status ?? 'approved') === 'approved';
  // The story begins with a markdown "### Title" line; lift it into a heading.
  const text = story?.story ?? '';
  const nl = text.indexOf('\n');
  const titleLine = (nl === -1 ? text : text.slice(0, nl)).replace(/^#+\s*/, '').trim();
  const bodyText = (nl === -1 ? '' : text.slice(nl + 1)).trim();

  return (
    <ContentLayout
      header={
        <Header
          variant="h1"
          actions={
            <Button iconName="arrow-left" onClick={() => navigate('/stories')}>
              Back to stories
            </Button>
          }
        >
          {[story?.make, story?.model].filter(Boolean).join(' ') || typeKey}
        </Header>
      }
    >
      <SpaceBetween size="l">
        {error && <Alert type="error">{error}</Alert>}
        {loading && <Box>Loading…</Box>}
        {story && (
          <Container
            header={
              <Header
                variant="h2"
                description={`${story.type_key}${
                  story.story_generated_at ? ` · generated ${story.story_generated_at.slice(0, 10)}` : ''
                }${story.story_model ? ` · ${story.story_model}` : ''}`}
                actions={
                  <SpaceBetween size="xs" direction="horizontal">
                    <Badge color={published ? 'green' : 'grey'}>
                      {story.story_status ?? 'approved'}
                    </Badge>
                    <Button
                      loading={busy}
                      onClick={() => void setStatus(published ? 'rejected' : 'approved')}
                    >
                      {published ? 'Unpublish' : 'Publish'}
                    </Button>
                  </SpaceBetween>
                }
              >
                {titleLine || 'Story'}
              </Header>
            }
          >
            <SpaceBetween size="m">
              {story.story_judge && (
                <Alert
                  type={story.story_judge.approved ? 'success' : 'warning'}
                  header={`Auto-review: ${
                    story.story_judge.approved ? 'passed — published' : 'held for review'
                  }`}
                >
                  {story.story_judge.reason || '—'}
                  {story.story_judge.verbatim_overlap
                    ? ` · verbatim overlap flagged: “${story.story_judge.verbatim_overlap}”`
                    : ''}
                </Alert>
              )}
              <Box variant="p" fontSize="body-m">
                <span style={{ whiteSpace: 'pre-wrap' }}>{bodyText || text}</span>
              </Box>
              {story.story_highlights && story.story_highlights.length > 0 && (
                <div>
                  <Box variant="h4">Highlights</Box>
                  <ul>
                    {story.story_highlights.map((h, i) => (
                      <li key={i}>{h}</li>
                    ))}
                  </ul>
                </div>
              )}
              {story.notable_missions && story.notable_missions.length > 0 && (
                <div>
                  <Box variant="h4">Notable missions</Box>
                  <ul>
                    {story.notable_missions.map((m, i) => (
                      <li key={i}>{m}</li>
                    ))}
                  </ul>
                </div>
              )}
              {story.story_sources && story.story_sources.length > 0 && (
                <Box variant="small" color="text-body-secondary">
                  Sources:{' '}
                  {story.story_sources.map((src, i) => (
                    <span key={i}>
                      {i > 0 && ' · '}
                      <Link href={src.url} external fontSize="body-s">
                        {src.title}
                      </Link>
                    </span>
                  ))}
                </Box>
              )}
            </SpaceBetween>
          </Container>
        )}
      </SpaceBetween>
    </ContentLayout>
  );
};

export default StoryDetailPage;
